import type { ErrorLike } from "@apollo/client";

/**
 * The per-field messages a failed mutation carries.
 *
 * The API returns them keyed by property path — `testimonials.1.title`,
 * `workSamples.0.link` — which is what makes a forty-field form usable when it
 * fails. A single joined string is a log line, not something a user can act on.
 *
 * Returns an empty object for errors that carry none (network failures,
 * permission errors), so callers can always spread it.
 */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: unknown }> })
    ?.graphQLErrors;

  for (const graphQLError of graphQLErrors ?? []) {
    const fieldErrors = (graphQLError.extensions as { fieldErrors?: Record<string, string> })
      ?.fieldErrors;
    if (fieldErrors && Object.keys(fieldErrors).length > 0) return fieldErrors;
  }

  return {};
}

/**
 * A message worth showing.
 *
 * When the failure is per-field, the fields are the message — "Title is
 * required" under a toast that says "Couldn't save" tells someone nothing about
 * *which* title. Falls back to the error's own message.
 */
export function saveErrorMessage(error: unknown, fallback = "Couldn't save your changes."): string {
  const fieldErrors = fieldErrorsFrom(error);
  const entries = Object.entries(fieldErrors);

  if (entries.length > 0) {
    return entries
      .slice(0, 3)
      .map(([path, message]) => `${humanizePath(path)}: ${message}`)
      .join("\n") + (entries.length > 3 ? `\n…and ${entries.length - 3} more` : "");
  }

  return (error as ErrorLike)?.message || fallback;
}

/** `testimonials.1.title` → `Testimonial 2 title`. */
function humanizePath(path: string): string {
  return path
    .split(".")
    .map((part, index) => {
      if (/^\d+$/.test(part)) return String(Number(part) + 1);
      const spaced = part.replace(/([A-Z])/g, " $1").toLowerCase().trim();
      return index === 0 ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : spaced;
    })
    .join(" ");
}
